import { useForm, Controller } from "react-hook-form";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Select from 'react-select';
import { apiPost, apiGet } from "../../../utils/apiFetch";
import apiPath from "../../../utils/apiPath";
import useToastContext from "hooks/useToastContext";
import ErrorMessage from "components/ErrorMessage";

const customStyles = {
  control: (provided) => ({
    ...provided,
    borderColor: '#DFDFDF',
    borderWidth: '2px',
    borderRadius: '0.5rem',
    padding: '2px',
    minHeight: '40px',
    boxShadow: 'none',
    '&:hover': { borderColor: '#DFDFDF' }
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#e5e7eb' : 'white',
    color: state.isSelected ? 'white' : 'black',
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: '#e5e7eb',
    borderRadius: '0.25rem',
  })
};

const BackgroundCheckForm = ({ onBack, onCheckCreated, stats, employees, checkTypes, isLoadingLists }) => {
  const notification = useToastContext();
  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [priceMap, setPriceMap] = useState({});
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // 🟢 Verification state machine
  const [verifyState, setVerifyState] = useState('idle'); // idle | verifying | success | failed
  const [verifyError, setVerifyError] = useState('');
  const [createdCheck, setCreatedCheck] = useState(null);

  const {
    handleSubmit,
    control,
    watch,
    register,
    formState: { errors }
  } = useForm({ mode: "onChange" });

  const selectedEmployeeId = watch("employeeID");
  const selectedTypes = watch("checkTypeUUIDs") || [];
  const forceCreate = watch("forceCreate");
  const sendEmail = watch("sendEmail");

  const selectedEmployee = employees.find(e => e._id === selectedEmployeeId?.value);

  // ─────────────────────────────────────────────────────────
  // 🆕 Fetch prices for selected check types (by NAME)
  // ─────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchPrices = async () => {
      if (!selectedTypes.length) {
        setPriceMap({});
        return;
      }
      try {
        setIsLoadingPrice(true);
        const names = selectedTypes.map(t => t.label).join(',');
        const res = await apiGet(`${apiPath.getBackgroundCheckPrices}?names=${encodeURIComponent(names)}`);

        if (res?.data?.success) {
          setPriceMap(res.data.results?.priceMap || {});
        } else {
          setPriceMap({});
        }
      } catch (e) {
        console.error("Price fetch failed", e);
        setPriceMap({});
      } finally {
        setIsLoadingPrice(false);
      }
    };
    fetchPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTypes.length]);

  // ─────────────────────────────────────────────────────────
  // 🆕 Handle Stripe return — verify payment on mount
  // ─────────────────────────────────────────────────────────
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const txnId = searchParams.get('txn');
    const paymentStatus = searchParams.get('payment');

    if (paymentStatus !== 'success' || !sessionId || !txnId) return;

    setVerifyState('verifying');

    const finalize = async () => {
      try {
        const res = await apiGet(apiPath.verifyBgPayment, {
          session_id: sessionId,
          transactionId: txnId
        });

        if (res?.data?.success) {
          const newCheck = res.data.results?.check;
          const msg = res.data.message;

          if (msg === 'CREDIBLED_SUBMISSION_FAILED') {
            setCreatedCheck(newCheck);
            setVerifyState('failed');
            setVerifyError(
              'Payment succeeded but the background check could not be submitted to the provider. ' +
              'Our team has been notified. Please contact support with your transaction ID: ' +
              (newCheck?.payment?.transactionId || 'N/A')
            );
          } else {
            setCreatedCheck(newCheck);
            setVerifyState('success');
            notification.success("Payment successful! Background check initiated.");
          }
        } else {
          setVerifyState('failed');
          setVerifyError(res?.data?.message || 'Payment verification failed');
        }
      } catch (err) {
        console.error('Verify failed:', err);
        setVerifyState('failed');
        setVerifyError('Something went wrong while verifying your payment. Please contact support.');
      } finally {
        // Clear query params after a short delay so refresh doesn't retrigger
        setTimeout(() => setSearchParams({}), 3000);
      }
    };

    finalize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────────────────────────────────────────────────────
  // Pricing computation
  // ─────────────────────────────────────────────────────────
  const priceBreakdown = selectedTypes.map(t => ({
    name: t.label,
    price: priceMap[t.label] ?? null
  }));
  const totalPrice = priceBreakdown.reduce((sum, x) => sum + (x.price || 0), 0);
  const hasMissingPrice = priceBreakdown.some(x => x.price === null);

  // ─────────────────────────────────────────────────────────
  // Pay & Initiate
  // ─────────────────────────────────────────────────────────
  const handlePayAndInitiate = async () => {
    if (hasMissingPrice) {
      notification.error("One or more selected checks have no price configured.");
      return;
    }

    const payload = {
      employeeID: selectedEmployeeId.value,
      employeeData: {
        firstName: selectedEmployee?.Employee?.name?.split(' ')[0] || 'Unknown',
        lastName: selectedEmployee?.Employee?.name?.split(' ').slice(1).join(' ') || 'Unknown',
        email: selectedEmployee?.Employee?.email || '',
        country: selectedEmployee?.EmployeeCountry?.name || ''
      },
      checkTypeUUIDs: selectedTypes.map(opt => opt.value),
      checkTypeDetails: selectedTypes.map(opt => ({
        uuid: opt.value,
        name: opt.label,
        description: opt.description,
        value: opt.value
      })),
      forceCreate: forceCreate || false,
      sendEmail: sendEmail !== undefined ? sendEmail : true
    };

    setIsLoading(true);
    try {
      const res = await apiPost(apiPath.createBgCheckout, payload);
      if (res?.data?.success) {
        window.location.href = res.data.results.sessionUrl;
      } else {
        notification.error(res?.data?.message || "Failed to create checkout session");
      }
    } catch (err) {
      console.error('Checkout error:', err);
      notification.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const employeeOptions = employees.map(e => ({
    value: e._id,
    label: `${e?.Employee?.name} (${e?.Employee?.email})`,
    country: e?.EmployeeCountry?.name,
  }));

  const checkTypeOptions = checkTypes.map(ct => ({
    value: ct.uuid,
    label: ct.name,
    description: ct.description,
    priority: ct.priority
  }));

  // ─────────────────────────────────────────────────────────
  // 🟢 FULL-SCREEN VERIFICATION STATES (before the form renders)
  // ─────────────────────────────────────────────────────────
  if (verifyState !== 'idle') {
    return (
      <div className="container mx-auto px-4 py-16 flex items-center justify-center min-h-[70vh]">
        <div className="bg-white rounded-lg shadow-lg p-10 max-w-lg w-full text-center">

          {/* VERIFYING */}
          {verifyState === 'verifying' && (
            <>
              <div className="flex justify-center mb-6">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-LightBlue"></div>
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Processing your payment…
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Please don't close this window. We're initiating your background check with the provider.
              </p>
              <p className="text-xs text-gray-400">
                This usually takes 10–30 seconds for multi-check orders.
              </p>
            </>
          )}

          {/* SUCCESS */}
          {verifyState === 'success' && (
            <>
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Background Check Initiated!
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Payment received and the check has been submitted. The candidate will receive an invitation email shortly.
              </p>
              <button
                onClick={() => {
                  setVerifyState('idle');
                  if (createdCheck && onCheckCreated) onCheckCreated(createdCheck);
                  else onBack();
                }}
                className="bg-LightBlue text-white px-6 py-2.5 rounded shadow hover:shadow-lg"
              >
                Go to Check List
              </button>
            </>
          )}

          {/* FAILED */}
          {verifyState === 'failed' && (
            <>
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Payment Successful, Submission Delayed
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                {verifyError}
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => {
                    setVerifyState('idle');
                    onBack();
                  }}
                  className="bg-LightBlue text-white px-6 py-2.5 rounded shadow hover:shadow-lg"
                >
                  View Check List
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────
  // NORMAL FORM
  // ─────────────────────────────────────────────────────────
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-6">Initiate New Background Check</h2>

          {isLoadingLists ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <form>
              <div className="space-y-6">
                {/* Candidate */}
                <div className="z-10 w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Candidate <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="employeeID"
                    control={control}
                    rules={{ required: "Candidate is required" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={employeeOptions}
                        styles={customStyles}
                        placeholder="Search or select a candidate..."
                        isSearchable={true}
                        isClearable={true}
                        className="basic-single-select"
                        classNamePrefix="select"
                      />
                    )}
                  />
                  <ErrorMessage message={errors?.employeeID?.message} />
                </div>

                {/* Check Types */}
                <div className="z-0 w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Check Types <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="checkTypeUUIDs"
                    control={control}
                    rules={{ required: "At least one check type is required" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={checkTypeOptions}
                        isMulti={true}
                        isSearchable={true}
                        styles={customStyles}
                        placeholder="Search and select multiple checks..."
                        formatOptionLabel={(data) => (
                          <div className="flex flex-col">
                            <span className="font-semibold">{data.label}</span>
                            <span className="text-xs text-gray-500">{data.description || 'No description'}</span>
                          </div>
                        )}
                      />
                    )}
                  />
                  <ErrorMessage message={errors?.checkTypeUUIDs?.message} />
                </div>

                {/* Toggles */}
                <div className="flex flex-wrap items-center gap-6 pt-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="forceCreate"
                      {...register("forceCreate")}
                      className="w-4 h-4 text-LightBlue rounded border-gray-300 focus:ring-LightBlue"
                    />
                    <label htmlFor="forceCreate" className="text-sm text-gray-700 cursor-pointer">
                      Bypass duplicate check (Force create)
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="sendEmail"
                      {...register("sendEmail")}
                      defaultChecked={true}
                      className="w-4 h-4 text-LightBlue rounded border-gray-300 focus:ring-LightBlue"
                    />
                    <label htmlFor="sendEmail" className="text-sm text-gray-700 cursor-pointer">
                      Send invitation email to candidate
                    </label>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-4 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => { control._reset(); setShowPreview(false); }}
                    className="px-6 py-2.5 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={onBack}
                    className="px-6 py-2.5 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit(() => setShowPreview(true))}
                    disabled={isLoading || !selectedEmployeeId || selectedTypes.length === 0}
                    className="bg-LightBlue text-white font-medium text-sm px-8 py-2.5 rounded shadow hover:shadow-lg disabled:opacity-50"
                  >
                    Preview & Initiate
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Quick Stats Panel */}
      <div>
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Quick Stats</h2>
          <div className="space-y-3">
            <div className="flex justify-between p-3 bg-blue-50 rounded text-blue-700">
              <span>Total Checks</span>
              <span className="font-bold">{stats.total}</span>
            </div>
            <div className="flex justify-between p-3 bg-green-50 rounded text-green-700">
              <span>Completed</span>
              <span className="font-bold">{stats.completed}</span>
            </div>
            <div className="flex justify-between p-3 bg-yellow-50 rounded text-yellow-700">
              <span>In Progress</span>
              <span className="font-bold">{stats.inProgress}</span>
            </div>
            <div className="flex justify-between p-3 bg-red-50 rounded text-red-700">
              <span>Action Required</span>
              <span className="font-bold">{stats.actionRequired}</span>
            </div>
          </div>
        </div>
      </div>

      {/* PREVIEW & CONFIRMATION MODAL WITH PRICING */}
      {showPreview && selectedEmployee && selectedTypes.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold">Confirm Background Check</h3>
              <p className="text-sm text-gray-500">Review details and pay to initiate.</p>
            </div>

            <div className="p-6 space-y-4">
              {/* Candidate */}
              <div>
                <h4 className="font-semibold text-gray-700 border-b pb-2">Candidate Details</h4>
                <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Name:</span>{' '}
                    <span className="font-medium">{selectedEmployee?.Employee.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>{' '}
                    <span className="font-medium">{selectedEmployee?.Employee.email}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Country:</span>{' '}
                    <span className="font-medium">{selectedEmployee?.EmployeeCountry.name || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Selected Checks with prices */}
              <div>
                <h4 className="font-semibold text-gray-700 border-b pb-2">
                  Selected Checks ({selectedTypes.length})
                </h4>
                <ul className="mt-2 space-y-2">
                  {selectedTypes.map((opt, idx) => {
                    const price = priceMap[opt.label];
                    return (
                      <li key={idx} className="bg-gray-50 p-3 rounded border text-sm">
                        <div className="flex justify-between items-start gap-3">
                          <div className="flex-1">
                            <div className="font-medium">{opt.label}</div>
                            <div className="text-gray-500 text-xs">
                              {opt.description || 'No description available'}
                            </div>
                          </div>
                          <div className="font-semibold text-gray-900 whitespace-nowrap">
                            {price !== undefined
                              ? `$${Number(price).toFixed(2)}`
                              : <span className="text-red-500 text-xs">Not priced</span>}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Total */}
              <div className="bg-blue-50 border border-blue-100 rounded p-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 font-medium">Total Amount</span>
                  <span className="text-2xl font-bold text-blue-700">
                    {isLoadingPrice ? '...' : `$${totalPrice.toFixed(2)}`}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Currency: USD. You will be redirected to Stripe to complete payment.
                </p>
                {hasMissingPrice && (
                  <p className="text-xs text-red-600 mt-1">
                    ⚠ Some selected checks have no price configured. Contact admin.
                  </p>
                )}
              </div>

              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                <p><strong>Force Create:</strong> {forceCreate ? "Yes" : "No"}</p>
                <p><strong>Send Email:</strong> {sendEmail !== false ? "Yes" : "No"}</p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handlePayAndInitiate}
                disabled={isLoading || isLoadingPrice || hasMissingPrice}
                className="px-6 py-2 bg-LightBlue text-white rounded shadow hover:shadow-lg disabled:opacity-50"
              >
                {isLoading ? "Redirecting..." : `Pay $${totalPrice.toFixed(2)} & Initiate`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackgroundCheckForm;